import { useI18n } from '../../i18n';
// ============================================
// PurchaseOrdersPage - Main Purchase Orders Page
// Procurement & Inventory Management view
// ============================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import type { Product, StockImport } from '@conai/shared-types';
import { PageHeader } from '../../components/shared/PageHeader';
import { Pagination } from '../../components/shared/Pagination';
import { PageContainer } from '../../components/shared/PageContainer';
import { ExportButton } from '../../components/shared/ExportButton';
import { showToast } from '../../components/shared';
import { MoreVerticalIcon } from '../../components/icons';
import { useExport } from '../../hooks/useExport';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import {
  useStockImports,
  useSuppliers,
  useCreateStockImport,
  useProducts,
  useSubmitStockImport,
  useApproveStockImport,
  useRejectStockImport,
  useStartReceivingStockImport,
  useCancelStockImport,
  useCompleteStockImport,
  useStockImport,
} from '../../hooks';
import { useStoreSelectionStore } from '../../stores';
import { useActiveCurrency } from '../../hooks/useActiveCurrency';
import { useCurrencyScope, useMoney } from '../../providers/CurrencyProvider';
import { getApiErrorDetails, getApiErrorMessage, translateOrFallback } from '../../utils/apiError';
import {
  PurchaseOrdersFilterBento,
  OrderCodeCell,
  formatOrderCode,
  PurchaseStatusBadge,
  mapBackendStatusToUI,
  type BackendStockImportStatus,
  FulfillmentVelocityChart,
  SmartInsightsCard,
  RecentActivityFeed,
  type PurchaseOrderStatus,
  type FulfillmentBar,
  type ActivityItem,
} from './components';

// ============================================
// Types
// ============================================

/**
 * Nhà cung cấp như API trả về cho dropdown.
 *
 * Bug #174: API trả trạng thái ở `is_active` (boolean), một số endpoint cũ còn
 * kèm `status` dạng chuỗi — đọc cả hai để không phụ thuộc vào một dạng duy nhất.
 */
type SupplierOption = {
  id: string;
  name: string;
  is_active?: boolean | null;
  status?: string | null;
};

/**
 * Coi là active khi và chỉ khi dữ liệu nói vậy.
 *
 * Bug #174 (vòng 2): việc LỌC dropdown giờ do server làm (`?is_active=true`),
 * helper này KHÔNG còn lọc danh sách nữa. Nó chỉ còn một việc: quyết định có
 * gắn nhãn "(Inactive)" cho nhà cung cấp đang được chọn sẵn hay không — bản ghi
 * đó lấy từ danh sách CHUNG nên vẫn phải tự đọc trạng thái.
 *
 * KHÔNG mặc định "thiếu field thì coi như inactive": gắn nhãn sai vào một nhà
 * cung cấp bình thường còn khó hiểu hơn là không gắn gì. Vì thế khi cả hai field
 * đều vắng mặt thì rơi về `is_active !== false`.
 */
function isSupplierActive(supplier: SupplierOption): boolean {
  const rawStatus = (supplier.status ?? '').toString().trim().toLowerCase();
  if (rawStatus === 'inactive' || rawStatus === 'disabled' || rawStatus === 'archived') {
    return false;
  }
  if (rawStatus === 'active') return true;
  return supplier.is_active !== false;
}

interface PurchaseOrder {
  id: string;
  code: string;
  backendStatus: BackendStockImportStatus;
  storeName: string;
  supplierId?: string;
  supplierName: string;
  quantityItems: number;
  totalAmount: number;
  receivedAmount: number;
  status: PurchaseOrderStatus;
  expectedDate: Date | null;
  createdAt: Date;
  /**
   * Bug #159: payload có trả `created_at` thật không. `createdAt` ở trên KHÔNG
   * phân biệt được — thiếu field thì toDateOrNow trả "bây giờ", và một phiếu
   * "tạo bây giờ" rơi ra ngoài mọi khoảng ngày quá khứ người dùng chọn. Đó là
   * lý do bộ lọc ngày "không hoạt động" khi list serializer bỏ field này.
   */
  hasCreatedAt: boolean;
}

interface DraftItemForm {
  productId: string;
  expectedQuantity: number;
  unitCost: string;
}

interface CompleteItemForm {
  productId: string;
  productName: string;
  expectedQuantity: number;
  receivedQuantity: number;
}

type DraftApiItemPayload = {
  product: string;
  quantity: number;
  unit_cost: string;
};

type DraftApiPayloadV2 = {
  store: string;
  supplier_id?: string;
  expected_date?: string;
  notes?: string;
  items: DraftApiItemPayload[];
};

type ListEnvelope<T> = {
  results?: T[];
  items?: T[];
  data?: T[] | { results?: T[]; items?: T[]; data?: T[] };
  count?: number;
  total?: number;
  meta?: { count?: number };
};

function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  const data = payload as ListEnvelope<T> | undefined;
  if (!data) {
    return [];
  }

  if (Array.isArray(data.data)) {
    return data.data;
  }

  if (data.data && typeof data.data === 'object') {
    return data.data.data ?? data.data.results ?? data.data.items ?? [];
  }

  return data.results ?? data.items ?? [];
}

function safeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function safeNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

/**
 * Tổng tiền của một phiếu nhập.
 *
 * Bug #149/#150 — BE chốt 19/08/2026:
 * - `total_cost` CÓ ở cả list và detail (annotate `Sum(quantity * unit_cost)`),
 *   trả về dạng CHUỖI decimal `"1234.50"`. Cộng thẳng bằng `+` là nối chuỗi, nên
 *   bắt buộc đi qua `safeNumber` trước khi vào thẻ tổng.
 * - Nguyên nhân thẻ trống không phải lệch tên field: `unit_cost` nullable, phiếu
 *   tạo không kèm đơn giá thì mọi dòng NULL, `SUM` bỏ qua NULL, tổng ra `"0.00"`
 *   mà không lỗi ở đâu. BE đã cho `add_items` fallback về `product.cost_price` và
 *   có lệnh backfill cho phiếu cũ.
 *
 * Vì vậy KHÔNG dò các tên `total_amount`/`total_value`/`grand_total` (không tồn
 * tại) và không tự cộng lại từ `items` (list serializer không trả `items`, mà
 * nếu có thì cũng cùng `unit_cost` NULL — cộng tay chỉ ra 0 lần nữa).
 *
 * Cặp với `resolveOrderReceivedTotal` bên dưới — thẻ hiển thị CẢ HAI.
 */
function resolveOrderTotal(order: unknown): number {
  const raw = (order ?? {}) as Record<string, unknown>;
  return safeNumber(raw.total_cost, 0);
}

/**
 * Giá trị THỰC NHẬN của phiếu: `received_total_cost` = received_quantity ×
 * unit_cost (BE 19/08/2026, có ở cả list và detail, cũng là chuỗi decimal).
 *
 * Không phải cách khác để đo cùng một thứ với `total_cost` mà là số khác hẳn:
 * phiếu chưa nhận hàng (DRAFT/SUBMITTED/APPROVED) luôn bằng 0, phiếu nhận thiếu
 * thì nhỏ hơn số đặt. Đặt cạnh nhau mới thấy được chênh lệch đặt/nhận — gộp làm
 * một là mất đúng thông tin cần cho việc đối soát NCC.
 */
function resolveOrderReceivedTotal(order: unknown): number {
  const raw = (order ?? {}) as Record<string, unknown>;
  return safeNumber(raw.received_total_cost, 0);
}

/** Parse ngày an toàn — chuỗi rỗng/không hợp lệ trả về "bây giờ" thay vì Invalid Date. */
function toDateOrNow(value: unknown): Date {
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

/** Payload có mang ngày parse được không — phân biệt ngày THẬT với fallback của toDateOrNow. */
function isParseableDate(value: unknown): boolean {
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  return !Number.isNaN(new Date(value).getTime());
}

type RowActionKey = 'submit' | 'approve' | 'start' | 'complete' | 'reject' | 'cancel';

function getNextActionLabel(status: BackendStockImportStatus, t: (key: string) => string): string {
  if (status === 'DRAFT') return t('submitForApproval');
  if (status === 'SUBMITTED') return t('approveOrReject');
  if (status === 'APPROVED') return t('startReceiving');
  if (status === 'RECEIVING') return t('completeReceiving');
  if (status === 'COMPLETED') return t('done');
  if (status === 'CANCELLED' || status === 'REJECTED') return t('recreateDraft');
  return 'N/A';
}

function getPrimaryAction(status: BackendStockImportStatus, t: (key: string) => string): { key: RowActionKey; label: string } | null {
  if (status === 'DRAFT') return { key: 'submit', label: t('submit') || 'Submit' };
  if (status === 'SUBMITTED') return { key: 'approve', label: t('approve') || 'Approve' };
  if (status === 'APPROVED') return { key: 'start', label: t('start') || 'Start' };
  if (status === 'RECEIVING') return { key: 'complete', label: t('complete') || 'Complete' };
  return null;
}

// Bug #193 (cùng bệnh): hai nhãn này trước đây là chuỗi tiếng Anh viết cứng nên
// không đổi theo ngôn ngữ giao diện.
function getSecondaryActions(
  status: BackendStockImportStatus,
  t: (key: string) => string
): Array<{ key: RowActionKey; label: string }> {
  if (status === 'SUBMITTED') return [{ key: 'reject', label: t('reject') || 'Reject' }];
  if (status === 'DRAFT') return [{ key: 'cancel', label: t('cancel') || 'Cancel' }];
  return [];
}

// Bug #3: labels phải qua i18n — build options từ t() thay vì hardcode English.
function getStatusOptions(t: (key: string) => string) {
  return [
    { value: 'DRAFT', label: t('draft') },
    { value: 'SUBMITTED', label: t('submitted') },
    { value: 'APPROVED', label: t('approved') },
    { value: 'REJECTED', label: t('rejected') },
    { value: 'RECEIVING', label: t('receiving') },
    { value: 'COMPLETED', label: t('completed') },
    { value: 'CANCELLED', label: t('cancelled') },
  ];
}

const EMPTY_ACTIVITY: ActivityItem[] = [];

const INSIGHT = 'liveProcurementDataIsNowConnected';

// ============================================
// Component
// ============================================

export function PurchaseOrdersPage() {
  const { t } = useI18n();
  const statusOptions = useMemo(() => getStatusOptions(t), [t]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedStoreId } = useStoreSelectionStore();

  // Tiền tệ theo cửa hàng đang chọn (sheet 2 #75) — phiếu nhập luôn thuộc một
  // cửa hàng, nên các thẻ giá trị/tổng tiền của trang này đi theo nó.
  useCurrencyScope(useActiveCurrency());
  const money = useMoney();

  const mode = searchParams.get('mode');
  const storeIdFromQuery = searchParams.get('storeId');
  const productIdsFromQuery = (searchParams.get('productIds') ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  const suggestedQtyMap = useMemo(() => {
    const raw = searchParams.get('suggestedQtys') ?? '';
    const map = new Map<string, number>();

    raw
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .forEach((pair) => {
        const [productId, qtyRaw] = pair.split(':');
        const qty = Number(qtyRaw);
        if (productId && Number.isFinite(qty) && qty > 0) {
          map.set(productId, qty);
        }
      });

    return map;
  }, [searchParams]);
  const effectiveStoreId = storeIdFromQuery ?? selectedStoreId ?? null;

  // Filter state
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [actionableOnly, setActionableOnly] = useState(mode === 'replenishment');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  // Quick draft form state
  const [showDraftPanel, setShowDraftPanel] = useState(mode === 'replenishment');
  const [draftSupplierId, setDraftSupplierId] = useState<string | null>(null);
  const [draftExpectedDate, setDraftExpectedDate] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftItems, setDraftItems] = useState<DraftItemForm[]>(
    productIdsFromQuery.length > 0
      ? productIdsFromQuery.map((productId) => ({
          productId,
          expectedQuantity: suggestedQtyMap.get(productId) ?? 1,
          unitCost: '1',
        }))
      : [{ productId: '', expectedQuantity: 1, unitCost: '1' }]
  );

  const createStockImport = useCreateStockImport();
  const submitStockImport = useSubmitStockImport();
  const approveStockImport = useApproveStockImport();
  const rejectStockImport = useRejectStockImport();
  const startReceivingStockImport = useStartReceivingStockImport();
  const cancelStockImport = useCancelStockImport();
  const completeStockImport = useCompleteStockImport();

  const [completeOrderId, setCompleteOrderId] = useState<string | null>(null);
  const [completeItems, setCompleteItems] = useState<CompleteItemForm[]>([]);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  // Bug #160: hộp nhập lý do từ chối phiếu.
  const [rejectOrderId, setRejectOrderId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  useLockBodyScroll(!!detailOrderId || !!completeOrderId || !!rejectOrderId);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [highlightOrderId, setHighlightOrderId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);

  const isAnyActionPending =
    submitStockImport.isPending ||
    approveStockImport.isPending ||
    rejectStockImport.isPending ||
    startReceivingStockImport.isPending ||
    cancelStockImport.isPending ||
    completeStockImport.isPending ||
    createStockImport.isPending;

  const draftValidationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!effectiveStoreId) {
      errors.push(t('validationStoreContextRequired'));
    }
    if (!draftSupplierId) {
      errors.push(t('validationSupplierRequired'));
    }
    if (draftItems.length === 0) {
      errors.push(t('validationAtLeastOneItemRequired'));
    }

    draftItems.forEach((item, index) => {
      const row = index + 1;
      if (!item.productId) {
        errors.push(t('validationRowProductRequired', { row }));
      }
      if (!Number.isFinite(item.expectedQuantity) || item.expectedQuantity <= 0) {
        errors.push(t('validationRowExpectedQuantityPositive', { row }));
      }
      const cost = parseFloat(item.unitCost);
      if (!Number.isFinite(cost) || cost <= 0) {
        errors.push(t('validationRowUnitCostPositive', { row }));
      }
    });

    const duplicateSet = new Set<string>();
    for (const item of draftItems) {
      if (!item.productId) {
        continue;
      }
      if (duplicateSet.has(item.productId)) {
        errors.push(t('validationDuplicateDraftProducts'));
        break;
      }
      duplicateSet.add(item.productId);
    }

    return errors;
  }, [effectiveStoreId, draftSupplierId, draftItems, t]);

  const isDraftFormValid = draftValidationErrors.length === 0;

  const { data: completeOrderDetail, isLoading: isLoadingCompleteOrder } = useStockImport(
    completeOrderId ?? '',
    { enabled: !!completeOrderId }
  );

  const { data: detailOrder, isLoading: isLoadingDetailOrder } = useStockImport(
    detailOrderId ?? '',
    { enabled: !!detailOrderId }
  );

  useEffect(() => {
    if (mode === 'replenishment') {
      setShowDraftPanel(true);
    }
  }, [mode]);

  useEffect(() => {
    if (!highlightOrderId) {
      return;
    }

    const timer = window.setTimeout(() => {
      setHighlightOrderId(null);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [highlightOrderId]);

  useEffect(() => {
    setOpenActionMenuId(null);
  }, [currentPage, selectedStatus, selectedSupplier, actionableOnly, dateRange]);

  useEffect(() => {
    if (!openActionMenuId) {
      return;
    }

    const onWindowClick = () => setOpenActionMenuId(null);
    window.addEventListener('click', onWindowClick);
    return () => window.removeEventListener('click', onWindowClick);
  }, [openActionMenuId]);

  useEffect(() => {
    if (!completeOrderDetail) {
      return;
    }

    // Bug #158: `product` có thể là UUID string HOẶC object {id, name} tuỳ
    // serializer. Bản cũ gán thẳng `raw.product` vào productId, nên khi BE trả
    // object thì payload complete gửi đi là `product_id: {…}` — BE không khớp
    // được sản phẩm nào và KHÔNG cộng tồn kho, trong khi phiếu vẫn chuyển sang
    // COMPLETED. Nhìn từ ngoài đúng như "nhập thành công nhưng Inventory không đổi".
    const rows = extractList<Record<string, unknown>>(
      (completeOrderDetail as unknown as { items?: unknown }).items ?? completeOrderDetail
    );

    setCompleteItems(
      rows.map((raw) => {
        const productObj =
          raw.product && typeof raw.product === 'object'
            ? (raw.product as Record<string, unknown>)
            : null;

        const productId =
          safeString(raw.product_id) ||
          safeString(productObj?.id) ||
          (typeof raw.product === 'string' ? raw.product.trim() : '');

        const expected = safeNumber(
          raw.expected_quantity ?? raw.quantity ?? raw.expected_qty,
          0
        );

        return {
          productId,
          productName:
            safeString(raw.product_name) ||
            safeString(productObj?.name) ||
            safeString(raw.product_sku) ||
            t('unknownProduct'),
          expectedQuantity: expected,
          receivedQuantity: expected,
        };
      })
      // Không có product_id thì gửi lên cũng vô nghĩa — loại ra để không âm thầm
      // hoàn tất một phiếu mà chẳng ghi được dòng tồn kho nào.
      .filter((item) => item.productId.length > 0)
    );
  }, [completeOrderDetail, t]);

  // Bug #159 (vòng 2): khoảng ngày đẩy XUỐNG SERVER qua `created_at__gte/__lte`
  // (có sẵn trong StockImportListParams). Lọc client theo `createdAt` không đủ
  // tin được: list serializer có lúc không trả `created_at`, khi đó toDateOrNow
  // gán "bây giờ" cho mọi phiếu và mọi khoảng ngày quá khứ đều ra rỗng — đúng
  // triệu chứng "filter ngày không hoạt động". Server lọc trên cột DB nên không
  // phụ thuộc serializer trả gì.
  const dateFilterParams = useMemo(() => {
    const params: { created_at__gte?: string; created_at__lte?: string } = {};
    if (dateRange[0]) {
      const from = new Date(dateRange[0]);
      from.setHours(0, 0, 0, 0);
      params.created_at__gte = from.toISOString();
    }
    if (dateRange[1]) {
      const to = new Date(dateRange[1]);
      to.setHours(23, 59, 59, 999);
      params.created_at__lte = to.toISOString();
    }
    return params;
  }, [dateRange]);

  const {
    data: importsData,
    isLoading,
    error,
  } = useStockImports({
    store: effectiveStoreId ?? undefined,
    // Bug #160 (reviewer bắt): danh sách mặc định trước đây THIẾU `REJECTED` —
    // vừa từ chối xong là phiếu biến mất khỏi bảng, người dùng không thấy kết
    // quả việc mình vừa làm, trông y như "reject không chạy".
    status: selectedStatus ?? 'DRAFT,SUBMITTED,APPROVED,RECEIVING,COMPLETED,CANCELLED,REJECTED',
    supplier: selectedSupplier ?? undefined,
    ...dateFilterParams,
    page: 1,
    page_size: 1000,
  } as any);

  // Danh sách CHUNG — KHÔNG truyền `is_active`. Không có param nghĩa là BE trả
  // cả active lẫn inactive (bản ghi xoá mềm không bao giờ lọt ra, bất kể param),
  // đúng ý "All Status". Danh sách này dùng để resolve TÊN nhà cung cấp cho
  // phiếu cũ và đổ vào dropdown BỘ LỌC của bảng — bỏ inactive ở đây là không lọc
  // lại được phiếu của nhà cung cấp đã ngưng hợp tác.
  const { data: suppliersData } = useSuppliers({ page_size: 100 });

  // Bug #174 (vòng 2): danh sách RIÊNG cho dropdown form "New Import", lọc ngay
  // ở server. Bản cũ lấy chung `suppliersData` rồi lọc client-side — nhà cung
  // cấp active nằm từ bản ghi thứ 101 trở đi không bao giờ hiện ra.
  //
  // Chỉ được gửi boolean `true` cho `is_active`: BE nay trả 400 cho giá trị lạ
  // (`?is_active=abc`). Không truyền chuỗi, không truyền `null` — `cleanParams`
  // của api-client chỉ loại `undefined`, `null` vẫn lọt xuống query string.
  const { data: activeSuppliersData } = useSuppliers({ page_size: 200, is_active: true });
  const { data: productsData } = useProducts({
    page_size: 200,
    storeId: effectiveStoreId ?? undefined,
    status: 'ACTIVE',
  });
  const { data: allProductsData } = useProducts({
    page_size: 200,
    status: 'ACTIVE',
  });

  const importsList = useMemo(() => extractList<StockImport>(importsData), [importsData]);

  const supplierList = useMemo(
    () => extractList<SupplierOption>(suppliersData),
    [suppliersData]
  );

  const activeSupplierList = useMemo(
    () => extractList<SupplierOption>(activeSuppliersData),
    [activeSuppliersData]
  );

  const productsList = useMemo(() => {
    const scopedProducts = extractList<Product>(productsData);
    if (scopedProducts.length > 0 || !effectiveStoreId) {
      return scopedProducts;
    }
    return extractList<Product>(allProductsData);
  }, [productsData, allProductsData, effectiveStoreId]);

  // Bộ lọc trên bảng vẫn cần ĐỦ nhà cung cấp: phiếu cũ có thể thuộc nhà cung
  // cấp nay đã ngưng hợp tác, bỏ họ khỏi đây là không lọc lại được phiếu cũ.
  const supplierOptions = useMemo(
    () => supplierList.map((s) => ({ value: s.id, label: s.name })),
    [supplierList]
  );

  // Bug #174: dropdown ở form "New Import" CHỈ được hiện nhà cung cấp đang
  // active — tạo phiếu mới cho nhà cung cấp đã ngưng là sai nghiệp vụ. Nguồn là
  // `activeSupplierList` (server đã lọc), KHÔNG lọc lại ở client.
  //
  // Ngoại lệ: giá trị đang được chọn sẵn (mở lại/sửa một phiếu cũ) luôn được giữ
  // lại và đẩy lên đầu, nếu không thì select rơi về rỗng và người dùng mất dữ
  // liệu mà không hiểu vì sao. Nhãn "(Inactive)" chỉ gắn khi bản ghi trong danh
  // sách chung THẬT SỰ nói là inactive — supplier active nhưng nằm ngoài trang
  // của danh sách đã lọc thì không được dán nhãn sai.
  const draftSupplierOptions = useMemo(() => {
    const options = activeSupplierList.map((s) => ({ value: s.id, label: s.name }));

    if (draftSupplierId && !options.some((opt) => opt.value === draftSupplierId)) {
      const selected = supplierList.find((s) => s.id === draftSupplierId);
      if (selected) {
        options.unshift({
          value: selected.id,
          label: isSupplierActive(selected)
            ? selected.name
            : `${selected.name} (${t('inactive')})`,
        });
      }
    }

    return options;
  }, [activeSupplierList, supplierList, draftSupplierId, t]);

  const productOptions = useMemo(() => {
    return productsList.map((p) => ({
      value: p.id,
      label: `${p.sku} - ${p.name}`,
    }));
  }, [productsList]);

  const orders = useMemo<PurchaseOrder[]>(
    () =>
      importsList.map((order) => {
        // API thật trả `supplier` là UUID string (không phải `supplier_id`,
        // cũng không phải object) — resolve đủ 3 dạng, thiếu là filter supplier
        // loại sạch mọi dòng ("No orders found" dù API có data).
        const rawSupplier = (order as unknown as { supplier?: unknown }).supplier;
        const resolvedSupplierId =
          order.supplier_id ??
          (typeof rawSupplier === 'string'
            ? rawSupplier
            : (rawSupplier as { id?: string } | null)?.id) ??
          undefined;
        const matchedSupplier = supplierList.find(s => s.id === resolvedSupplierId);
        const supplierName =
          safeString((order as unknown as { supplier_name?: string | null }).supplier_name) ||
          safeString(order.supplier?.name) ||
          safeString(matchedSupplier?.name) ||
          t('noSupplier');

        return {
          id: String(order.id),
          code:
            safeString((order as unknown as { import_code?: string }).import_code) ||
            safeString(order.import_number) ||
            safeString((order as unknown as { code?: string; order_number?: string; import_code?: string }).code) ||
            safeString((order as unknown as { code?: string; order_number?: string; import_code?: string }).order_number) ||
            safeString((order as unknown as { code?: string; order_number?: string; import_code?: string }).import_code) ||
            String(order.id),
          backendStatus: order.status,
          storeName:
            safeString((order as unknown as { store_name?: string }).store_name) ||
            safeString(order.store?.name) ||
            t('unknownStore'),
          supplierId: resolvedSupplierId,
          supplierName,
          quantityItems: safeNumber((order as unknown as { items_count?: number }).items_count, order.total_items),
          // Bug #149/#150: cả hai field đều trả về CHUỖI decimal — xem
          // resolveOrderTotal, luôn phải parse trước khi cộng.
          totalAmount: resolveOrderTotal(order),
          receivedAmount: resolveOrderReceivedTotal(order),
          status: mapBackendStatusToUI(order.status),
          expectedDate: order.expected_date ? new Date(order.expected_date) : null,
          // created_at thiếu -> new Date(undefined) là Invalid Date, mọi phép so
          // sánh ngày sau đó đều false và đơn biến mất khỏi mọi thống kê.
          createdAt: toDateOrNow(order.created_at),
          hasCreatedAt: isParseableDate(order.created_at),
        };
      }),
    [importsList, supplierList, t]
  );

  // Filter orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    if (selectedStatus) {
      result = result.filter((o) => o.backendStatus === selectedStatus);
    }

    if (selectedSupplier) {
      result = result.filter((o) => o.supplierId === selectedSupplier);
    }

    if (actionableOnly) {
      result = result.filter((o) =>
        o.backendStatus === 'DRAFT' ||
        o.backendStatus === 'SUBMITTED' ||
        o.backendStatus === 'APPROVED' ||
        o.backendStatus === 'RECEIVING'
      );
    }

    // Bug #159: server đã lọc khoảng ngày (created_at__gte/__lte, xem
    // dateFilterParams) — đây là lưới nhất quán cho BE cũ chưa hỗ trợ param.
    // Hai điểm sống còn:
    // - Hai đầu khoảng là NGÀY, `createdAt` là THỜI ĐIỂM: đầu cuối phải nới tới
    //   23:59:59.999, nếu không "khoảng đúng một ngày" loại sạch phiếu tạo sau
    //   nửa đêm — bảng trống dù ngày đó có phiếu.
    // - Phiếu KHÔNG có `created_at` thật (hasCreatedAt=false, toDateOrNow đã gán
    //   "bây giờ") thì CHO QUA, tin kết quả server. Đem "bây giờ" đi so là loại
    //   nhầm mọi phiếu khỏi mọi khoảng quá khứ — chính là lỗi "filter ngày
    //   không hoạt động".
    if (dateRange[0]) {
      const from = new Date(dateRange[0]);
      from.setHours(0, 0, 0, 0);
      result = result.filter((o) => !o.hasCreatedAt || o.createdAt >= from);
    }
    if (dateRange[1]) {
      const to = new Date(dateRange[1]);
      to.setHours(23, 59, 59, 999);
      result = result.filter((o) => !o.hasCreatedAt || o.createdAt <= to);
    }

    return result;
  }, [orders, selectedStatus, selectedSupplier, actionableOnly, dateRange]);

  // Giá trị nhập hàng trong tháng — ĐẶT và THỰC NHẬN.
  //
  // Bug #149: ngoài 'cancelled', phiếu bị TỪ CHỐI cũng không phải tiền đã cam kết
  // nên không được cộng vào tổng giá trị nhập hàng trong tháng.
  //
  // Mốc "trong tháng" tính theo `created_at` — MỘT tập phiếu duy nhất, hai cách
  // đo. Cố ý không dùng `received_date` cho số thực nhận: hai mốc khác nhau thì
  // hai con số nằm cạnh nhau không còn so được với nhau, và phiếu chưa COMPLETED
  // không có `received_date` nên sẽ rơi khỏi tổng mà không ai thấy. Nếu QA đối
  // chiếu thực tế thấy mốc nhận hàng đúng hơn thì đổi ở đúng chỗ này.
  //
  // `orders` là tập ĐÃ QUA bộ lọc server (status/supplier, và từ vòng 2 là cả
  // khoảng ngày) — thẻ tổng từ trước tới nay vẫn phản ánh bộ lọc đang bật, ngày
  // chỉ nhập chung một hành vi đó chứ không đổi nghĩa thẻ.
  const mtdValues = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const inScope = orders.filter((o) => {
      if (o.createdAt < startOfMonth) return false;
      const backend = String(o.backendStatus ?? '').toUpperCase();
      return o.status !== 'cancelled' && backend !== 'CANCELLED' && backend !== 'REJECTED';
    });

    return {
      ordered: inScope.reduce((sum, o) => sum + o.totalAmount, 0),
      received: inScope.reduce((sum, o) => sum + o.receivedAmount, 0),
    };
  }, [orders]);

  // Client-side pagination over the (server-filtered) full result set.
  /** Có bộ lọc nào đang bật không — quyết định câu chữ của trạng thái rỗng. */
  const hasActiveFilters = Boolean(
    selectedStatus || selectedSupplier || actionableOnly || dateRange[0] || dateRange[1]
  );

  const handleClearFilters = () => {
    setSelectedStatus(null);
    setSelectedSupplier(null);
    setActionableOnly(false);
    setDateRange([null, null]);
    setCurrentPage(1);
  };

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, currentPage, pageSize]);

  // Bug #147: biểu đồ phải chạy trên `filteredOrders` — trước đây dùng `orders`
  // (chưa lọc) nên chọn khoảng ngày ở bộ lọc mà biểu đồ đứng yên, người dùng
  // thấy đúng như "không vào được ngày đã chọn".
  const velocityData: FulfillmentBar[] = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const counts = days.map((day, idx) => {
      const value = filteredOrders.filter((o) => o.createdAt.getDay() === ((idx + 1) % 7)).length;
      return { day, value };
    });
    const avg = counts.length > 0 ? Math.round(counts.reduce((sum, d) => sum + d.value, 0) / counts.length) : 0;
    return [...counts, { day: 'Avg', value: avg }];
  }, [filteredOrders]);

  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format currency — dùng chung utils/currency để không lệch đơn vị với các trang khác.
  const formatCurrency = (amount: number) => money.format(amount);

  // Handlers
  const handleNewOrder = () => {
    setShowDraftPanel((prev) => !prev);
  };

  const handleDraftItemChange = (index: number, patch: Partial<DraftItemForm>) => {
    setDraftItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const handleAddDraftItem = () => {
    setDraftItems((prev) => [...prev, { productId: '', expectedQuantity: 1, unitCost: '1' }]);
  };

  const handleRemoveDraftItem = (index: number) => {
    setDraftItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleCreateDraftImport = async () => {
    if (!effectiveStoreId || !draftSupplierId || !isDraftFormValid) {
      return;
    }

    const items = draftItems
      .filter((item) => item.productId && item.expectedQuantity > 0)
      .map((item) => ({
        product_id: item.productId,
        expected_quantity: item.expectedQuantity,
        unit_cost: item.unitCost,
      }));

    if (items.length === 0) {
      return;
    }

    const payloadV2: DraftApiPayloadV2 = {
      store: effectiveStoreId,
      supplier_id: draftSupplierId,
      expected_date: draftExpectedDate || undefined,
      notes: draftNotes || undefined,
      items: items.map((item) => ({
        product: item.product_id,
        quantity: item.expected_quantity,
        unit_cost: item.unit_cost,
      })),
    };

    const payloadLegacy = {
      store_id: effectiveStoreId,
      supplier_id: draftSupplierId,
      expected_date: draftExpectedDate || undefined,
      notes: draftNotes || undefined,
      items,
    };

    /**
     * Payload V2 bị từ chối vì BE còn dùng TÊN FIELD CŨ hay vì dữ liệu sai?
     *
     * Chỉ trường hợp đầu mới được gửi lại bằng payload legacy. Dấu hiệu là BE
     * nhắc tới đúng những tên cũ (`store_id`, `product_id`, `expected_quantity`)
     * — trước đây chỗ này kiểm `error instanceof Error`, mà lỗi của api-client
     * là OBJECT thuần chứ không phải `Error`, nên nhánh này KHÔNG BAO GIỜ chạy.
     *
     * Cố ý soi chuỗi THÔ của server (`message` + `error_details[].message` +
     * đường dẫn field), không dùng câu đã dịch: đây là phép dò khả năng tương
     * thích của API, không phải câu cho người đọc.
     */
    const shouldFallbackToLegacy = (error: unknown): boolean => {
      const raw = [
        error instanceof Error ? error.message : '',
        typeof (error as { message?: unknown })?.message === 'string'
          ? (error as { message: string }).message
          : '',
        ...getApiErrorDetails(error).flatMap((detail) => [detail.field ?? '', detail.message]),
      ]
        .join(' ')
        .toLowerCase();

      return (
        raw.includes('store_id') ||
        raw.includes('product_id') ||
        raw.includes('expected_quantity')
      );
    };

    /**
     * Nhãn của từng ô trong form nháp, để câu lỗi chỉ đúng chỗ cần sửa.
     *
     * Khoá dạng `items[].product` khớp MỌI dòng của mảng — `apiError` tra
     * đường dẫn nguyên văn trước, rồi tới dạng template này. Khai cả tên V2
     * lẫn tên legacy vì payload nào cũng có thể là payload bị từ chối.
     *
     * Qua `translateOrFallback` để nhãn không bao giờ là tên key thô, kể cả khi
     * key mới chưa kịp merge vào locale.
     */
    const label = (key: string, fallbackText: string) => translateOrFallback(t, key, fallbackText);
    const draftFieldLabels: Record<string, string> = {
      store: label('fieldLabelStore', 'Store'),
      store_id: label('fieldLabelStore', 'Store'),
      supplier: label('supplier', 'Supplier'),
      supplier_id: label('supplier', 'Supplier'),
      expected_date: label('expectedDate', 'Expected Date'),
      notes: label('notes', 'Notes'),
      items: label('fieldLabelItems', 'Items'),
      'items[].product': label('product', 'Product'),
      'items[].product_id': label('product', 'Product'),
      'items[].quantity': label('quantity', 'Quantity'),
      'items[].expected_quantity': label('quantity', 'Quantity'),
      'items[].unit_cost': label('unitCost', 'Unit Cost'),
    };

    let newImport;
    try {
      newImport = await createStockImport.mutateAsync(payloadV2 as unknown as Parameters<typeof createStockImport.mutateAsync>[0]);
    } catch (error) {
      if (shouldFallbackToLegacy(error)) {
        newImport = await createStockImport.mutateAsync(
          payloadLegacy as unknown as Parameters<typeof createStockImport.mutateAsync>[0]
        );
      } else {
        // Câu lỗi lấy từ `error_details[].field/code` — không còn đoán field
        // bằng cách so chuỗi tiếng Anh của DRF. BE cũ (chưa có `error_details`)
        // vẫn rơi về `message` như trước, nên không vỡ trước khi QA cập nhật.
        showToast(
          getApiErrorMessage(error, t, {
            fieldLabels: draftFieldLabels,
            fallback: t('failedToCreateDraftImport'),
          }),
          'error'
        );
        return;
      }
    }

    setCurrentPage(1);
    setHighlightOrderId(newImport.id);
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);

    setShowDraftPanel(false);
    setDraftNotes('');
    setDraftExpectedDate('');
    setDraftItems([{ productId: '', expectedQuantity: 1, unitCost: '1' }]);
  };

  const handleOrderClick = (orderId: string) => {
    setDetailOrderId(orderId);
  };

  const handleRowAction = (orderId: string) => {
    setDetailOrderId(orderId);
  };

  // Các hook đã có onError hiện toast (bug #160). try/catch ở đây chỉ để
  // `mutateAsync` bị từ chối không thành unhandled rejection.
  const handleStatusAction = async (order: PurchaseOrder) => {
    try {
      if (order.backendStatus === 'DRAFT') {
        await submitStockImport.mutateAsync(order.id);
        return;
      }
      if (order.backendStatus === 'SUBMITTED') {
        await approveStockImport.mutateAsync(order.id);
        /**
         * Bug #246 — vào bằng badge "Stock awaiting" rồi duyệt phiếu cuối cùng
         * thì người dùng bị bỏ lại trước một bảng trống.
         *
         * Diễn biến: badge đặt bộ lọc trạng thái = SUBMITTED. Duyệt xong, phiếu
         * chuyển sang APPROVED nên rơi khỏi bộ lọc; nếu đó là phiếu chờ duyệt
         * cuối cùng thì danh sách rỗng VÀ badge cũng biến mất (pendingCount về
         * 0). Không còn gì trên màn chỉ ra rằng đang có một bộ lọc bật — người
         * dùng đọc ra là "duyệt xong thì mất sạch phiếu".
         *
         * Vừa duyệt hết việc thì bỏ bộ lọc, đưa về danh sách đầy đủ — nơi phiếu
         * vừa duyệt đang nằm đó với trạng thái APPROVED, đúng thứ cần nhìn thấy
         * để biết thao tác đã ăn.
         *
         * Chỉ làm khi đúng là phiếu chờ duyệt cuối cùng: còn phiếu khác thì giữ
         * nguyên bộ lọc để duyệt tiếp.
         */
        const remainingPending = orders.filter(
          (o) => o.backendStatus === 'SUBMITTED' && o.id !== order.id
        ).length;
        if (selectedStatus === 'SUBMITTED' && remainingPending === 0) {
          setSelectedStatus(null);
          setCurrentPage(1);
        }
        return;
      }
      if (order.backendStatus === 'APPROVED') {
        await startReceivingStockImport.mutateAsync(order.id);
        return;
      }
      if (order.backendStatus === 'RECEIVING') {
        setCompleteOrderId(order.id);
      }
    } catch {
      // đã báo qua toast
    }
  };

  const handleRejectOrCancel = async (order: PurchaseOrder) => {
    // Bug #160: Reject giờ mở hộp nhập lý do thay vì gọi thẳng API không kèm gì.
    // `POST /stock-imports/{id}/reject/` nhận `reason`, và một phiếu bị từ chối
    // mà không ai biết vì sao thì người tạo phiếu không sửa được để gửi lại.
    if (order.backendStatus === 'SUBMITTED') {
      setRejectOrderId(order.id);
      setRejectReason('');
      return;
    }
    if (order.backendStatus === 'DRAFT') {
      try {
        await cancelStockImport.mutateAsync({ id: order.id });
      } catch {
        // đã báo qua toast
      }
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectOrderId) return;
    try {
      await rejectStockImport.mutateAsync({
        id: rejectOrderId,
        reason: rejectReason.trim() || undefined,
      });
      setRejectOrderId(null);
      setRejectReason('');
    } catch {
      // Giữ hộp thoại mở để người dùng sửa lý do và thử lại — toast đã nói lỗi gì.
    }
  };

  const handleRowActionByKey = async (order: PurchaseOrder, action: RowActionKey) => {
    setOpenActionMenuId(null);
    if (action === 'submit' || action === 'approve' || action === 'start' || action === 'complete') {
      await handleStatusAction(order);
      return;
    }
    if (action === 'reject' || action === 'cancel') {
      await handleRejectOrCancel(order);
    }
  };

  const handleUpdateCompleteItem = (index: number, quantity: number) => {
    setCompleteItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, receivedQuantity: Math.max(0, quantity) }
          : item
      )
    );
  };

  const handleSubmitComplete = async () => {
    if (!completeOrderId) {
      return;
    }

    // Bug #158: hoàn tất mà không gửi được dòng nào thì tồn kho không tăng, còn
    // phiếu vẫn thành COMPLETED — hỏng âm thầm. Báo lỗi thay vì return trong im lặng.
    if (completeItems.length === 0) {
      showToast(t('completeNoItemsError'), 'error');
      return;
    }

    try {
      await completeStockImport.mutateAsync({
        id: completeOrderId,
        data: {
          items: completeItems.map((item) => ({
            product_id: item.productId,
            received_quantity: item.receivedQuantity,
          })),
        },
      });
    } catch {
      // Hook đã hiện toast lỗi. Giữ modal mở để người dùng sửa số lượng và thử
      // lại — đóng modal ở đây là xoá mất thứ duy nhất họ có thể tác động.
      return;
    }

    setCompleteOrderId(null);
    setCompleteItems([]);
  };

  const hasOverReceivedItems = useMemo(
    () => completeItems.some((item) => item.receivedQuantity > item.expectedQuantity),
    [completeItems]
  );

  const handleActivityClick = (activityId: string) => {
    // TODO: Navigate to activity reference
    console.log('Activity click:', activityId);
  };

  const handleExitReplenishmentMode = () => {
    navigate(ADMIN_ROUTES.STOCK_IMPORTS);
  };

  const { triggerExport, isExporting } = useExport({
    endpoint: '/stock-imports/',
    params: effectiveStoreId ? { store: effectiveStoreId } : {},
    filePrefix: 'purchase-orders',
  });

  return (
    <PageContainer>
      {/* Page Header */}
      <PageHeader
        /* MỘT nguồn cho tên màn: dùng chung key với mục nav (`navStockImports`).
           Trước đây tiêu đề dùng key riêng `purchaseOrders` nên nav ghi "Stock
           Imports" còn tiêu đề ghi "Purchase Orders" — cùng một màn, hai tên,
           và không có gì ràng buộc chúng khớp nhau khi ai đó sửa một bên.

           Chọn "Stock Imports" làm tên chuẩn vì đó là tên đã dùng ở mọi nơi
           khác: route `/stock-imports`, endpoint `/stock-imports/`, nút "New
           Import", mã phiếu `PO-IMP-…`. Đổi ba chỗ kia theo tiêu đề thì tốn
           hơn nhiều mà chẳng được gì. */
        title={t('navStockImports')}
        subtitle={t('procurementInventoryManagement')}
        actions={
          <div className="flex items-center gap-2">
            {/* Bộ đếm phiếu chờ duyệt (mục #39) — nhắc việc, bấm là lọc ngay
                SUBMITTED. Đếm trên `orders` (chưa qua filter) để không tự
                biến mất khi người dùng đang lọc trạng thái khác. */}
            {(() => {
              const pendingCount = orders.filter((o) => o.backendStatus === 'SUBMITTED').length;
              return pendingCount > 0 ? (
                <button
                  onClick={() => setSelectedStatus('SUBMITTED')}
                  className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-100"
                >
                  {t('pendingApprovalCount', { count: pendingCount })}
                </button>
              ) : null;
            })()}
            <ExportButton onExport={triggerExport} isLoading={isExporting} />
            <button
              onClick={handleNewOrder}
              className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-all hover:shadow-lg"
              style={{
                background: 'linear-gradient(165deg, #004AC6, #2563EB)',
                boxShadow: '0px 4px 12px rgba(37, 99, 235, 0.3)',
              }}
            >
              <span className="text-lg leading-none">+</span>
              {t('newImport')}
            </button>
          </div>
        }
      />

      {mode === 'replenishment' && (
        <div className="mt-4 rounded-xl border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-900">
          <p className="font-semibold">{t('openedFromInventoryReplenishment')}</p>
          <p className="mt-1 text-primary-700">
            {effectiveStoreId ? `Store context: ${effectiveStoreId}.` : 'No store selected.'} {productIdsFromQuery.length} product(s) prefilled for draft creation.
          </p>
          <button
            onClick={handleExitReplenishmentMode}
            className="mt-2 text-xs font-semibold text-primary-700 underline hover:text-primary-800"
          >
            Exit replenishment mode
          </button>
        </div>
      )}

      {!effectiveStoreId && (
        <div className="mt-4 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
          <p className="font-medium">{t('storeIsRequired')}</p>
          <p className="mt-1">{t('selectAStoreFromInventoryFirstOrSet')}</p>
        </div>
      )}

      {showDraftPanel && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-900">{t('createDraftImport')}</h3>
            <button
              onClick={() => setShowDraftPanel(false)}
              className="text-sm font-medium text-slate-500 hover:text-slate-700"
            >
              Close
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t('purchaseordersSupplier')}</label>
              <select
                value={draftSupplierId ?? ''}
                onChange={(e) => setDraftSupplierId(e.target.value || null)}
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
              >
                <option value="">{t('selectSupplier')}</option>
                {draftSupplierOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t('expectedDate')}</label>
              <input
                type="date"
                value={draftExpectedDate}
                onChange={(e) => setDraftExpectedDate(e.target.value)}
                className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t('purchaseordersStore')}</label>
              <input
                value={
                  useStoreSelectionStore.getState().availableStores.find(s => s.id === effectiveStoreId)?.name ??
                  effectiveStoreId ?? 
                  ''
                }
                disabled
                className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 font-semibold"
              />
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {draftItems.map((item, index) => (
              <div key={`${item.productId}-${index}`} className="flex flex-wrap items-end gap-3 md:flex-nowrap">
                <select
                  value={item.productId}
                  onChange={(e) => handleDraftItemChange(index, { productId: e.target.value })}
                  className="h-10 min-w-[150px] flex-1 rounded-lg border border-slate-200 px-3 text-sm"
                >
                  <option value="">{t('selectProduct')}</option>
                  {productOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="flex flex-col shrink-0">
                  <label className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">{t('quantity')}</label>
                  <input
                    type="number"
                    min={1}
                    value={item.expectedQuantity}
                    onChange={(e) => handleDraftItemChange(index, { expectedQuantity: Math.max(1, Number(e.target.value || 1)) })}
                    className="h-10 w-24 rounded-lg border border-slate-200 px-3 text-sm"
                  />
                </div>
                <div className="flex flex-col shrink-0">
                  <label className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">{t('unitCost')}</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={item.unitCost}
                    onChange={(e) => handleDraftItemChange(index, { unitCost: e.target.value })}
                    className="h-10 w-24 rounded-lg border border-slate-200 px-3 text-sm"
                  />
                </div>
                <button
                  onClick={() => handleRemoveDraftItem(index)}
                  className="h-10 rounded-lg border border-red-200 px-3 text-sm text-red-600 hover:bg-red-50 transition-colors shrink-0"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3">
            <button
              onClick={handleAddDraftItem}
              className="text-sm font-semibold text-primary-700 hover:text-primary-800"
            >
              Add item
            </button>
          </div>

          <div className="mt-4">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">{t('notes')}</label>
            <textarea
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder={t('optionalNotes')}
            />
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleCreateDraftImport}
              disabled={!isDraftFormValid || createStockImport.isPending}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createStockImport.isPending ? 'Creating...' : 'Create Draft Import'}
            </button>
          </div>

          {draftValidationErrors.length > 0 && (
            <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
              {draftValidationErrors.slice(0, 5).map((error) => (
                <p key={error}>- {error}</p>
              ))}
              {draftValidationErrors.length > 5 && (
                <p>...and {draftValidationErrors.length - 5} more validation issue(s).</p>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadPurchaseOrders')}</p>
          <p className="mt-1 text-red-600">
            {error instanceof Error ? error.message : 'An error occurred while loading purchase orders.'}
          </p>
        </div>
      )}

      {isLoading && (
        <div className="mt-6 rounded-xl bg-white border border-slate-100 p-8 shadow-sm">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </div>
      )}

      {isAnyActionPending && (
        <div className="mt-4 rounded-lg bg-blue-50 p-3 text-xs font-medium text-blue-700">
          Updating stock import workflow... please wait.
        </div>
      )}

      {/* Filter Bento Grid */}
      <div className="mt-8">
        <PurchaseOrdersFilterBento
          selectedSupplier={selectedSupplier}
          selectedStatus={selectedStatus}
          dateRange={dateRange}
          onSupplierChange={(value) => {
            setSelectedSupplier(value);
            setCurrentPage(1);
          }}
          onStatusChange={(value) => {
            setSelectedStatus(value);
            setCurrentPage(1);
            }}
            onDateRangeChange={(range) => {
              setDateRange(range);
              setCurrentPage(1);
            }}
            supplierOptions={supplierOptions}
            statusOptions={statusOptions}
            totalValue={mtdValues.ordered}
            receivedValue={mtdValues.received}
          />

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
            <input
              id="actionable-only"
              type="checkbox"
              checked={actionableOnly}
              onChange={(e) => setActionableOnly(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <label htmlFor="actionable-only" className="cursor-pointer select-none">
              {t('showActionableOrdersOnly')}
            </label>
          </div>
        </div>

        {/* Orders Table */}
        <div ref={tableRef} className="mt-8">
          <div className="overflow-hidden rounded-xl bg-white shadow-sm">
            {/* Cuộn ngang trên màn hình hẹp — grid cột cố định ~1360px */}
            <div className="overflow-x-auto">
            <div className="min-w-[1320px]">
            {/* Table Header */}
            <div className="grid grid-cols-[176px_150px_160px_92px_130px_150px_130px_130px_100px_48px] bg-[#F3F3FE] px-6 py-3">
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('orderCode')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('storeColumn')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('supplier')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('qtyItems')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('totalAmount')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('status')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('expectedColumn')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('creationDate')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[1px] text-[#64748B]">
                {t('actionsColumn')}
              </div>
              {/* Cột kebab (3 chấm) tách riêng, không cần tiêu đề */}
              <div />
            </div>

            {/* Table Body */}
            <div>
              {paginatedOrders.map((order) => (
                <div
                  key={order.id}
                  className={`grid grid-cols-[176px_150px_160px_92px_130px_150px_130px_130px_100px_48px] items-center border-t border-[rgba(241,245,249,0.5)] px-6 py-4 ${highlightOrderId === order.id ? 'bg-primary-50' : ''}`}
                >
                  {/* Order Code */}
                  <div>
                    <OrderCodeCell
                      code={formatOrderCode(order.code)}
                      onClick={() => handleOrderClick(order.id)}
                    />
                  </div>

                  {/* Store */}
                  <div className="text-sm text-[#191B23]">{order.storeName}</div>

                  {/* Supplier */}
                  <div className="text-sm font-medium text-[#191B23]">
                    {order.supplierName}
                  </div>

                  {/* Qty Items */}
                  <div className="text-sm text-[#191B23]">
                    {order.quantityItems}
                  </div>

                  {/* Total Amount */}
                  <div className="text-sm font-bold text-[#191B23]">
                    {formatCurrency(order.totalAmount)}
                  </div>

                  {/* Status */}
                  <div>
                    <PurchaseStatusBadge status={order.backendStatus} />
                    <p className="mt-1 text-[10px] font-medium text-slate-500">
                      {t('nextActionLabel')}{getNextActionLabel(order.backendStatus, t)}
                    </p>
                  </div>

                  {/* Expected Date */}
                  <div className="text-sm text-[#64748B]">
                    {order.expectedDate ? formatDate(order.expectedDate) : '-'}
                  </div>

                  {/* Creation Date */}
                  <div className="text-sm text-[#64748B]">
                    {formatDate(order.createdAt)}
                  </div>

                  {/* Actions: nút hành động chính (Approve/Complete/Submit/Start) */}
                  <div className="flex justify-start">
                    {getPrimaryAction(order.backendStatus, t) && (
                      <button
                        onClick={() => handleRowActionByKey(order, getPrimaryAction(order.backendStatus, t)!.key)}
                        disabled={isAnyActionPending}
                        className="rounded-lg border border-primary-200 px-2 py-1 text-[11px] font-semibold text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {getPrimaryAction(order.backendStatus, t)!.label}
                      </button>
                    )}
                  </div>

                  {/* Menu 3 chấm — cột riêng cuối hàng */}
                  <div className="relative flex justify-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (openActionMenuId === order.id) {
                          setOpenActionMenuId(null);
                        } else {
                          setOpenActionMenuId(order.id);
                        }
                      }}
                      className="rounded-lg p-1.5 text-[#64748B] transition-colors hover:bg-slate-100"
                    >
                      <MoreVerticalIcon size={16} />
                    </button>

                    {openActionMenuId === order.id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-0 top-8 z-20 w-36 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
                      >
                        {getSecondaryActions(order.backendStatus, t).map((action) => (
                          <button
                            key={action.key}
                            onClick={() => handleRowActionByKey(order, action.key)}
                            disabled={isAnyActionPending}
                            className="w-full rounded-md px-2 py-1.5 text-left text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {action.label}
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            setOpenActionMenuId(null);
                            handleRowAction(order.id);
                          }}
                          className="w-full rounded-md px-2 py-1.5 text-left text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          {t('viewDetails') || 'View details'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Empty State */}
              {/* Bug #246 — trạng thái rỗng phải nói ĐANG LỌC GÌ và cho đường
                  quay lại. "Try adjusting your filters" bằng tiếng Anh cứng
                  không chỉ ra bộ lọc nào đang bật, cũng chẳng bấm được vào đâu;
                  gặp nó ngay sau một thao tác duyệt thì người dùng hiểu thành
                  "vừa làm hỏng cái gì đó". */}
              {paginatedOrders.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="mb-4 h-16 w-16 rounded-full bg-slate-100" />
                  <p className="text-sm font-medium text-[#191B23]">{t('noOrdersFound')}</p>
                  {hasActiveFilters ? (
                    <>
                      <p className="mt-1 text-xs text-[#64748B]">{t('ordersHiddenByFilter')}</p>
                      <button
                        onClick={handleClearFilters}
                        className="mt-3 rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        {t('clearFilters')}
                      </button>
                    </>
                  ) : (
                    <p className="mt-1 text-xs text-[#64748B]">{t('noOrdersYet')}</p>
                  )}
                </div>
              )}
            </div>
            </div>
            </div>

            {/* Pagination */}
            <div className="border-t border-slate-100 px-6 py-4">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredOrders.length / pageSize)}
                totalItems={filteredOrders.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                itemLabel="orders"
              />
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 grid grid-cols-12 gap-6">
          {/* Left - Fulfillment Velocity Chart (col-span-8) */}
          <div className="col-span-12 lg:col-span-8">
            <FulfillmentVelocityChart data={velocityData} />
          </div>

          {/* Right - Smart Insights + Recent Activity (col-span-4) */}
          <div className="col-span-12 space-y-6 lg:col-span-4">
            <SmartInsightsCard body={t(INSIGHT)} />
            <RecentActivityFeed
              items={EMPTY_ACTIVITY}
              onItemClick={handleActivityClick}
            />
          </div>
        </div>

        {detailOrderId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">{t('stockImportDetails')}</h3>
                <button
                  onClick={() => setDetailOrderId(null)}
                  className="text-sm font-medium text-slate-500 hover:text-slate-700"
                >
                  Close
                </button>
              </div>

              {isLoadingDetailOrder ? (
                <div className="py-10 text-center text-sm text-slate-500">{t('loadingDetails')}</div>
              ) : detailOrder ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 p-4 md:grid-cols-2">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('importNumber')}</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {safeString((detailOrder as unknown as { import_code?: string }).import_code) || detailOrder.import_number || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('purchaseordersStatus')}</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{detailOrder.status}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('purchaseordersStore')}</p>
                      <p className="mt-1 text-sm text-slate-800">
                        {safeString((detailOrder as unknown as { store_name?: string }).store_name) || detailOrder.store?.name || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('purchaseordersSupplier')}</p>
                      <p className="mt-1 text-sm text-slate-800">
                        {safeString((detailOrder as unknown as { supplier_name?: string | null }).supplier_name) || detailOrder.supplier?.name || 'No Supplier'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('expectedDate')}</p>
                      <p className="mt-1 text-sm text-slate-800">{detailOrder.expected_date ? formatDate(new Date(detailOrder.expected_date)) : '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('totalCost')}</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {/* `total_amount` không tồn tại ở stock-imports (BE
                            19/08/2026) — đọc cùng một field với bảng và thẻ tổng. */}
                        {formatCurrency(resolveOrderTotal(detailOrder))}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200">
                    <div className="grid grid-cols-[1.5fr_1fr_1fr] bg-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <div>{t('purchaseordersProduct')}</div>
                      <div>{t('expectedQty')}</div>
                      <div>{t('unitCost')}</div>
                    </div>
                    <div>
                      {(detailOrder.items ?? []).map((item) => {
                        const raw = item as unknown as { product_name?: string; product_sku?: string; unit_cost?: string | number };
                        const productName = raw.product_name ?? item.product?.name ?? raw.product_sku ?? item.product?.sku ?? item.product_id ?? '-';
                        const unitCost = raw.unit_cost != null && raw.unit_cost !== '' ? `${Number(raw.unit_cost).toFixed(2)}` : '-';
                        return (
                        <div key={item.id} className="grid grid-cols-[1.5fr_1fr_1fr] border-t border-slate-100 px-4 py-3 text-sm text-slate-800">
                          <div className="min-w-0 break-words pr-4 line-clamp-2" title={productName}>{productName}</div>
                          <div>{item.expected_quantity}</div>
                          <div>{unitCost}</div>
                        </div>
                        );
                      })}
                    </div>
                  </div>

                  {detailOrder.notes && (
                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('notes')}</p>
                      <p className="mt-1 text-sm text-slate-700">{detailOrder.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-10 text-center text-sm text-slate-500">{t('noDetailsFound')}</div>
              )}
            </div>
          </div>
        )}

        {/* Bug #160: hộp nhập lý do từ chối phiếu nhập */}
        {rejectOrderId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-bold text-slate-900">{t('rejectImportTitle')}</h3>
              <p className="mt-1 text-sm text-slate-500">{t('rejectImportDesc')}</p>

              <label className="mt-4 mb-1 block text-sm text-slate-600">{t('reason')}</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder={t('rejectImportReasonPlaceholder')}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />

              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={() => { setRejectOrderId(null); setRejectReason(''); }}
                  disabled={rejectStockImport.isPending}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 disabled:opacity-60"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleConfirmReject}
                  disabled={rejectStockImport.isPending}
                  className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {rejectStockImport.isPending ? t('processing') : t('reject')}
                </button>
              </div>
            </div>
          </div>
        )}

        {completeOrderId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">{t('completeReceiving')}</h3>
                <button
                  onClick={() => setCompleteOrderId(null)}
                  className="text-sm font-medium text-slate-500 hover:text-slate-700"
                >
                  Close
                </button>
              </div>

              {isLoadingCompleteOrder ? (
                <div className="py-10 text-center text-sm text-slate-500">{t('loadingOrderItems')}</div>
              ) : completeItems.length === 0 ? (
                /* Bug #158: trước đây gặp trường hợp này thì modal rỗng trơn, nút
                   mờ, không một lời giải thích — người dùng đóng lại và tưởng đã
                   nhập kho xong. Nói thẳng là không có dòng nào để nhận. */
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm font-medium text-amber-800">
                  {t('completeNoItemsError')}
                </div>
              ) : (
                <div className="space-y-3">
                  {completeItems.map((item, index) => (
                    <div key={`${item.productId}-${index}`} className="grid grid-cols-1 items-center gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-[1.5fr_1fr_1fr] sm:gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{item.productName}</p>
                        <p className="text-xs text-slate-500">Product ID: {item.productId}</p>
                      </div>
                      <div className="text-sm text-slate-700">Expected: {item.expectedQuantity}</div>
                      <input
                        type="number"
                        min={0}
                        value={item.receivedQuantity}
                        onChange={(e) => handleUpdateCompleteItem(index, Number(e.target.value || 0))}
                        className={`h-9 w-full min-w-0 rounded-lg border px-3 text-sm ${item.receivedQuantity > item.expectedQuantity ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}
                      />
                    </div>
                  ))}

                  {hasOverReceivedItems && (
                    <div className="rounded-lg bg-amber-50 p-3 text-xs font-medium text-amber-700">
                      Warning: some received quantities are greater than expected quantities.
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 flex justify-end">
                <button
                  onClick={handleSubmitComplete}
                  disabled={completeItems.length === 0 || completeStockImport.isPending}
                  className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {completeStockImport.isPending ? 'Completing...' : 'Confirm Complete'}
                </button>
              </div>
            </div>
          </div>
        )}
    </PageContainer>
  );
}
